<template>
  <b-sidebar
    id="add-new-crew-rate-sidebar"
    :visible="isActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => $emit('update:is-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">
          {{ crewRateData.id ? 'Update' : 'Add New' }} Crew Rate
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="
            resetForm();
            hide();
          "
        />
      </div>

      <!-- BODY -->
      <validation-observer ref="newCrewRateForm">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="validateForm()"
        >
          <b-form-group
            label="Number of Crew"
            label-for="number-crew"
          >
            <validation-provider
              #default="{ errors }"
              name="Number of Crew"
              rules="required"
            >
              <b-form-input
                id="number-crew"
                v-model="crewRateData.numOfCrew"
                :state="errors.length > 0 ? false : null"
                name="number-crew"
                type="number"
                step=".01"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Crew Rate"
            label-for="crew-rate"
          >
            <validation-provider
              #default="{ errors }"
              name="Rate"
              rules="required"
            >
              <b-form-input
                id="crew-rate"
                v-model="crewRateData.rate"
                :state="errors.length > 0 ? false : null"
                name="crew-rate"
                type="number"
                step=".01"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Office Rate"
            label-for="gta-office-rate"
          >
            <validation-provider
              #default="{ errors }"
              name="Office Rate"
              rules="required"
            >
              <b-form-input
                id="gta-office-rate"
                v-model="crewRateData.officeFee"
                :state="errors.length > 0 ? false : null"
                name="gta-office-rate"
                type="number"
                step=".01"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Driver Rate"
            label-for="driver-rate"
          >
            <validation-provider
              #default="{ errors }"
              name="Driver Rate"
              rules="required"
            >
              <b-form-input
                id="driver-rate"
                v-model="driverRateComputed"
                :state="errors.length > 0 ? false : null"
                name="driver-rate"
                disabled
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              variant="primary"
              class="mr-2"
              type="submit"
              :disabled="isLoading"
            >
              <b-spinner
                v-if="isLoading"
                small
                variant="light"
              />
              {{ crewRateData.id ? 'Update' : 'Add' }}
            </b-button>
            <b-button
              type="button"
              variant="outline-secondary"
              @click="
                resetForm();
                hide();
              "
            >
              Cancel
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSidebar,
  BSpinner,
} from 'bootstrap-vue';
import { computed, ref } from '@vue/composition-api';
import { required } from '@validations';
import { useToast } from 'vue-toastification/composition';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import useCrewRate from '@/modules/rate/composables/useCrewRate';
import vSelect from 'vue-select';

export default {
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    crewRate: {
      type: Object,
      required: true,
    },
  },
  setup(props, ctx) {
    const toast = useToast();
    const { createCrewRate, updateCrewRate } = useCrewRate();

    const crewRateData = ref(props.crewRate);
    const newCrewRateForm = ref(null);
    const isLoading = ref(false);

    const driverRateComputed = computed(
      () => crewRateData.value.rate - crewRateData.value.officeFee
    );

    const resetForm = () => {
      newCrewRateForm.value.reset();
    };

    const validateForm = async () => {
      if (isLoading.value) return;

      isLoading.value = true;

      const valid = await newCrewRateForm.value.validate();
      if (!valid) {
        isLoading.value = false;
        return;
      }

      try {
        crewRateData.value.id
          ? await updateCrewRate(crewRateData.value)
          : await createCrewRate(crewRateData.value);

        ctx.emit('form-submitted');
      } catch (error) {
        toast.error({
          component: ToastificationContent,
          props: {
            title: 'An error occurred.',
            icon: 'XCircleIcon',
          },
        });
      }

      isLoading.value = false;
    };

    return {
      // Reacitve
      crewRateData,
      newCrewRateForm,
      isLoading,

      // Computed
      driverRateComputed,

      // Method
      resetForm,
      validateForm,

      // Validator
      required,
    };
  },
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BSidebar,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    BSpinner,
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-crew-rate-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
